// utils/collaborativeEditor.ts

// Import necessary utilities and Firebase configurations
import { window } from "./window.util";
import { sanitise } from "./sanitise.util"; // Ensure this does not escape \n
import { auth, db, FirebaseFieldValue, firebaseImport, FirebaseTimestamp, storageRef } from "../../config/main_config";
import { nameFromUID } from "./nameFromUID.util";
import { waitForCompanyID } from "./waitForCompanyID.util";
import { platformDetails } from "../version.module";

// Define interfaces for Firestore data with dynamic keys
interface ContentData {
    [key: string]: {
        content: string;
    };
}

// Encapsulate all code within the CollaborativeEditor namespace
export namespace CollaborativeEditor {
    // Private variables and functions
    let editorDocRef: firebaseImport.firestore.DocumentReference<ContentData>;

    // Get DOM elements with appropriate type assertions
    let editor: HTMLTextAreaElement;
    let saveButton: HTMLButtonElement;
    let toggleEditingButton: HTMLButtonElement;
    let statusDiv: HTMLElement;
    let lastPageKey: string | null = window.location.hash.slice(2);

    // Flag to prevent infinite loops during updates
    let isUpdating: boolean = false;

    // Current editing state: false = editing disabled, true = editing enabled
    let isEditing: boolean = false;

    // Initial Ping
    let ping = false;

    /**
     * Displays status messages to the user.
     *
     * @param message - The message to display.
     * @param isError - Whether the message is an error (optional).
     */
    function displayStatus(message: string, isError: boolean = false): void {
        if (statusDiv) {
            statusDiv.textContent = message;
            statusDiv.style.color = isError ? 'red' : 'green';
            setTimeout(() => {
                statusDiv.textContent = '';
            }, 3000);
        } else {
            console.warn("StatusDiv is not defined.");
        }
    }

    /**
     * Loads the initial content from Firestore into the textarea.
     */
    function loadInitialContent(): void {
        editorDocRef.get().then((doc) => {
            if (doc.exists) {
                const data = doc.data() as ContentData;
                const key = window.location.hash.slice(2);
                if (data[key] && typeof data[key].content === 'string') {
                    editor.value = data[key].content;
                } else {
                    editor.value = 'Click to start editing...'; // Default to empty if content is missing
                }

                autoResizeTextarea();
            } else {
                // If no content exists, set initial content in Firestore
                const key = window.location.hash.slice(2);
                const initialContent = editor.value; // Ensure sanitization doesn't alter \n
                editorDocRef.set({ [key]: { content: initialContent } })
                    .then(() => {
                        displayStatus('Initial content saved.');
                        autoResizeTextarea();
                    })
                    .catch((error: Error) => {
                        console.error("Error initializing document:", error);
                        displayStatus('Error initializing document.', true);
                    });
            }
        }).catch((error: Error) => {
            console.error("Error fetching document:", error);
            displayStatus('Error fetching document.', true);
        });
    }

    /**
     * Listens for real-time updates from Firestore and updates the textarea accordingly.
     */
    function listenForUpdates(): void {
        editorDocRef.onSnapshot((doc) => {
            if (doc.exists) {
                const data = doc.data() as ContentData;
                const key = window.location.hash.slice(2);
                const newContent = data[key]?.content || 'Click to start editing...';

                if (newContent !== editor.value) { // Compare textarea values directly
                    isUpdating = true;

                    // Preserve the current caret position
                    const caretPosition = editor.selectionStart;

                    // Update the editor's content
                    editor.value = newContent;

                    // Restore the caret position
                    if (caretPosition !== null) {
                        editor.selectionStart = editor.selectionEnd = caretPosition;
                    }

                    isUpdating = false;
                    displayStatus('Content updated from another user.');
                }
            }
        }, (error: Error) => {
            console.error("Error listening for updates:", error);
            displayStatus('Error listening for updates.', true);
        });
    }

    /**
     * Saves the current content of the textarea to Firestore.
     */
    function saveContent(Save = null, Edit = null): void {
        if (ping) {
            const key = lastPageKey || window.location.hash.slice(2);
            const content = editor.value;
            console.log("Saving content:", content);

            editorDocRef.set({ [key]: { content: content } }, { merge: true })
                .then(() => {
                    displayStatus('Successfully saved content.');
                    
                    // Update the document version number
                    updateDocumentVersion();
                    
                    // Update the software revision number
                    updateRevisionNumber();
                })
                .catch((error: Error) => {
                    console.error("Error saving content:", error);
                    displayStatus('Error saving content.', true);
                });
        } else {
            console.warn("Initial Ping.");
            ping = true;
        }
    }
    
    /**
     * Updates the Management System Guide document version in all .deit files.
     */
    function updateDocumentVersion(): void {
        try {
            // Find the foot-note div in the current document
            const footNoteDiv = document.querySelector('.foot-note');
            if (footNoteDiv) {
                // Get the current text content
                const currentText = footNoteDiv.innerHTML;
                
                // Extract the version number using regex
                const versionMatch = currentText.match(/Management System Guide (\d+\.\d+)/);
                if (versionMatch && versionMatch[1]) {
                    // Parse the version number
                    const versionParts = versionMatch[1].split('.');
                    if (versionParts.length === 2) {
                        const major = parseInt(versionParts[0], 10);
                        let minor = parseInt(versionParts[1], 10);
                        
                        // Increment the minor version
                        minor += 1;
                        
                        // Create the new version string
                        const newVersion = `${major}.${minor}`;
                        
                        // Get current date in the format: 9 October 2024
                        const now = new Date();
                        const months = ['January', 'February', 'March', 'April', 'May', 'June',
                                        'July', 'August', 'September', 'October', 'November', 'December'];
                        const currentDate = `${now.getDate()} ${months[now.getMonth()]} ${now.getFullYear()}`;
                        
                        // Replace the version and date in the text
                        const newText = currentText.replace(
                            /Management System Guide \d+\.\d+ - Published: [^<]+/,
                            `Management System Guide ${newVersion} - Published: ${currentDate}`
                        );
                        
                        // Update the content of the foot-note div
                        footNoteDiv.innerHTML = newText;
                        
                        console.log(`Document version updated to: ${newVersion} (${currentDate})`);
                    }
                }
            }
        } catch (error) {
            console.error("Error updating document version:", error);
        }
    }
    
    /**
     * Updates both the version and revision numbers in the software footer after a successful save.
     */
    function updateRevisionNumber(): void {
        try {
            // Extract the current revision number
            const revisionStr = platformDetails.revision;
            const match = revisionStr.match(/^r(\d+)-/);
            
            if (match && match[1]) {
                const currentRevNum = parseInt(match[1], 10);
                const newRevNum = currentRevNum + 1;
                
                // Generate a random hash-like string (8 chars)
                const randomHash = Math.random().toString(36).substring(2, 10);
                
                // Update the revision in platformDetails
                platformDetails.revision = `r${newRevNum}-${randomHash}`;
                
                // Increment the version number as well
                const versionParts = platformDetails.version.split('.');
                if (versionParts.length === 3) {
                    const major = parseInt(versionParts[0], 10);
                    const minor = parseInt(versionParts[1], 10);
                    let patch = parseInt(versionParts[2], 10);
                    
                    // Increment the patch version
                    patch += 1;
                    
                    // Update the version
                    platformDetails.version = `${major}.${minor}.${patch}`;
                }
                
                // Update the footer with the new version info
                const footer = document.querySelector('#footer');
                if (footer) {
                    const versionParagraph = footer.querySelector('p');
                    if (versionParagraph) {
                        versionParagraph.textContent = `${platformDetails.platform} ${platformDetails.version} ${platformDetails.revision}`;
                    }
                }
                
                console.log(`Software version updated to: ${platformDetails.version} ${platformDetails.revision}`);
            }
        } catch (error) {
            console.error("Error updating version and revision numbers:", error);
        }
    }

    /**
     * Debounce function to limit how often a function can fire.
     *
     * @param func - The function to debounce.
     * @param wait - The debounce delay in milliseconds.
     * @returns A debounced version of the function.
     */
    function debounce(func: Function, wait: number): Function {
        let timeout: number | undefined;
        return function (...args: any[]) {
            const later = () => {
                timeout = undefined;
                func.apply(this, args);
            };
            if (timeout !== undefined) {
                clearTimeout(timeout);
            }
            timeout = window.setTimeout(later, wait);
        };
    }

    // Debounce saveContent to optimize performance
    //const debounceSaveContent = debounce(saveContent, 500);

    function autoResizeTextarea(): void {
        if (!editor) return;
        editor.style.height = 'auto';
        editor.style.height = `${editor.scrollHeight}px`;
    }

    /**
     * Handles input events from the textarea.
     * Automatically saves the content after debouncing.
     */
    function handleEditorInput(): void {
        if (isUpdating || !isEditing) return; // Prevent saving if updates are from Firestore or editing is disabled
        //debounceSaveContent(); // Automatically save on input with debounce
        autoResizeTextarea();
    }

    function handleHashChange(): void {
        const newPageKey = window.location.hash.slice(2);
        if (isUpdating) {
            console.warn('Data is currently being saved. Please wait before changing pages.');
            // Optionally trigger save logic here
        } else {
            lastPageKey = newPageKey;
            loadInitialContent();
        }
    }


    /**
     * Toggles the editing state of the textarea.
     */
    function toggleEditing(): void {
        isEditing = !isEditing;
        editor.disabled = !isEditing; // Disable or enable the textarea
        saveButton.disabled = !isEditing;
        if (isEditing) {
            toggleEditingButton.innerHTML = '<i class="fa-duotone fa-solid fa-save"></i>';
            displayStatus('Editing enabled.');
        } else {
            toggleEditingButton.innerHTML = '<i class="fa-duotone fa-solid fa-pencil"></i>';
            displayStatus('Editing disabled.');
        }
    }

    /**
     * Initializes event listeners for the editor and buttons.
     */
    async function initializeEventListeners(Save = null, Edit = null): Promise<void> {
        const companyID = await waitForCompanyID();
        editorDocRef = db.collection('customers')
            .doc(companyID)
            .collection('product')
            .doc('managementSystemGuide')
            .collection('editor')
            .doc('content') as firebaseImport.firestore.DocumentReference<ContentData>;

        editor = document.querySelector("#editor") as HTMLTextAreaElement;
        if (Save) { // Save button EventListener
            saveButton = Save as HTMLButtonElement;
            saveButton.addEventListener('click', () => saveContent(Save, Edit));
            console.log("Save button:", saveButton);
        }
        if (Edit) { // Edit button EventListener
            toggleEditingButton = Edit as HTMLButtonElement;
            toggleEditingButton.addEventListener('click', toggleEditing);
            console.log("Edit button:", toggleEditingButton);
        }
        statusDiv = document.getElementById('status') as HTMLElement;

        // Listen for changes in the editor and update Firestore
        editor.addEventListener('input', handleEditorInput);
        window.addEventListener('hashchange', handleHashChange);

        autoResizeTextarea();
    }

    /**
     * Sets the initial UI state based on the current editing state.
     */
    function updateUIBasedOnEditingState(): void {
        if (editor && saveButton && toggleEditingButton) { // Ensure elements are defined
            editor.disabled = !isEditing; // Disable or enable the textarea
            saveButton.disabled = !isEditing;
            toggleEditingButton.innerHTML = isEditing ?
                '<i class="fa-duotone fa-solid fa-save"></i>' :
                '<i class="fa-duotone fa-solid fa-pencil"></i>';
        } else {
            console.warn("One or more UI elements are not defined.");
        }
    }

    // Public API
    export async function init(Save = null, Edit = null): Promise<void> {
        await initializeEventListeners(Save, Edit);
        loadInitialContent();
        listenForUpdates();
        updateUIBasedOnEditingState(); // Set initial UI state
    }
    
    // Expose saveContent function for external use
    export function save(): void {
        saveContent();
    }
}